import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { ErrorMessage, Field, FormikProvider, useFormik } from 'formik';
import { toast } from 'react-toastify';
import Rellax from 'rellax';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import ReCAPTCHA from 'react-google-recaptcha';
import Popup from 'reactjs-popup';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Fade, Collapse } from '@mui/material';

import { getRssFeed } from '../../api/rss';

import airpodsRight from '../../assets/images/gifs/airpod-right.gif';
import airpodsLeft from '../../assets/images/gifs/airpod-left.gif';
import {
  boostInput,
  resize,
  getPartner,
  parseSessionStorage,
  adjustDownloadPrice,
} from '../../utils/utils';
import { createCampaign, updateCampaign } from '../../api/campaigns';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { signUp } from '../../api/auth';
import { payment } from '../../api/payment';
import Header from '../../components/Header/Header';
import { createStackAdaptCampaign } from '../../api/stackadapt';
import { Grid } from '@material-ui/core';
import TermsOfUse from '../../components/Footer/TermsOfUse';
import { attachDiscountToUser, getUserCard } from '../../api/users';
import { getReferrer } from '../../api/referrers';
import { validationSchema } from './validationSchema';
import { initialValues } from './initialValues';
import Advanced from '../../components/Advanced';
import CompressText from '../../components/CompressText';
import { useSuggestion } from '../../contexts/SuggestionContext';
import { StackAdaptPixel } from '../../utils/stackAdapt';
import { createErrorsLog } from '../../api/errorsLog';
import { useSessionStorageLogger } from '../../hooks/useSessionStorageLogger';
import { LOGS } from '../../utils/consts';
import { RangeSlider } from '../../components/RangeSlider/RangeSlider';
import CreditCard from '../../components/CreditCard/CreditCard';
import EpisodeDescription from '../../components/EpisodeDescription/EpisodeDescription';

const Modal = () => (
  <Popup
    trigger={open => (
      <button type="button" className="temrs-of-use">
        the terms of use
      </button>
    )}
    position="center center"
    modal
    closeOnDocumentClick
    lockScroll
  >
    {close => (
      <>
        <button type="button" className="close" onClick={close}>
          &times;
        </button>
        <TermsOfUse />
      </>
    )}
  </Popup>
);

const customMessageForRefer = {
  podfest: {
    title: 'Podfest discount',
    message: `Congrats on using the Podfest discount code! When you spend $100 you will get $120 worth of podcast downloads! (The normal price is $0.90, but you get your downloads for $.75) We’ll leave this up for a bit for our podfest friends, so be sure to use it a few times.`,
  },
  hubspot: {
    title: 'mowPod Boost + Hubspot',
    message: `Welcome to mowPod Boost in partnership with the Hubspot Podcast Network! Here you can get Boost at the discounted rate of $0.72 per download.`,
  },
};

const { partnerName, costPerDownload, url = '', howThisWorks, textBottom = '' } = getPartner();
export default function SignUp() {
  const [openEpisodes, setOpenEpisodes] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, setUser } = useCurrentUser();
  const history = useHistory();
  const textAreaRef = useRef(null);
  const [feed, setFeed] = useState({});
  const [savedPaymentMethod, setSavedPaymentMethod] = useState();
  const [discount, setDiscount] = useState();
  const [rssWithNewEpisode, setRssWithNewEpisode] = useState();
  const [paymentData, setPaymentData] = useState({});
  const location = useLocation();
  const { getSuggestions, setTitles, setSubtitles } = useSuggestion();
  const [campaign, setCampaign] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [showReferralUsedPopup, setShowReferralUsedPopup] = useState(false);
  const [pendingSignupData, setPendingSignupData] = useState(null);
  const [creditBalance, setCreditBalance] = useState(0);
  const [useCredit, setUseCredit] = useState(false);
  const [showRecaptcha, setShowRecaptcha] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  
  // Calculate final price after applying credit
  const calculateFinalPrice = (boost) => {
    if (referralCode && !showReferralUsedPopup) {
      // Referral discount
      return Math.max(0, boost - 50);
    } else if (useCredit && creditBalance > 0) {
      // Credit balance discount (only when user clicks USE CREDIT)
      return Math.max(0, boost - creditBalance);
    }
    return boost;
  };

  const formik = useFormik({
    initialValues: { ...initialValues, logined: !!user },
    validationSchema,
    validateOnChange: false,
    onSubmit: async values => {
      try {
        setLoading(true);
        let newUser = false;
        if (!user) {
          const { email, password } = values;
          try {
            const data = await signUp({
              email: email.toLowerCase(),
              password,
              source: partnerName,
              referralCode: referralCode,
              creditBalance,
            });
            setUser(data.user);
            newUser = data.isNewUser;
            setIsNewUser(data.isNewUser);
          } catch (error) {
            if (error.response?.data?.userExists) {
              // User already used referral, show popup
              setShowReferralUsedPopup(true);
              setPendingSignupData({ email, password, values });
              setUser(error.response.data.user);
              setLoading(false);
              localStorage.setItem('auth_token', error.response.data.token);
              return;
            }
            if (error.response?.data?.insufficientCredit) {
              // Requested credit exceeds available balance
              toast.error(error.response.data.message);
              setCreditBalance(error.response.data.availableCredit);
              setLoading(false);
              return;
            }
            throw error;
          }
        }

        if (discount) {
          await attachDiscountToUser(discount._id);
        }

        const { boost } = values;

        if (!values.episode.itunes.image) {
          values.episode.itunes.image = feed.itunes.image;
        }

        let createdCampaign;
        const fields = {
          ...values,
          boost,
          referrer: discount?._id,
          price: adjustDownloadPrice(discount?.price || costPerDownload, boost),
        };
        
        // Add credit balance usage if user clicked USE CREDIT
        if (useCredit && creditBalance > 0) {
          fields.credit_used = Math.min(creditBalance, boost);
        }
        
        if (!campaign) {
          createdCampaign = await createCampaign(fields);
        } else {
          await updateCampaign({_id: campaign._id, ...fields});
          createdCampaign = campaign;
        }

        logCampaignRunResponse(true);

        setCampaign(createdCampaign);
        let paymentResponse
        if (!paymentData.id) {
          paymentResponse = await payment({
            ...values,
            podcastTitle: feed.title,
            boost: calculateFinalPrice(createdCampaign.boost),
          });
          await updateCampaign({_id: createdCampaign._id, paymentId: paymentResponse.id,});
          setPaymentData(paymentResponse);
        }

        if (paymentResponse?.status === 'succeeded' || paymentData?.status === 'succeeded') {
          await createStackAdaptCampaign({ campaignId: createdCampaign._id, isNewUser: newUser });
          history.push(`/thank-you?campaignId=${createdCampaign._id}`);
        }

        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
        toast.error(error.response?.data?.message);

        logCreditCardResponseError(error.response.data?.message);

        if (error.response.status === 500) {
          logInternalServerError(LOGS.INTERNAL_SERVER_ERROR);
        }
      }
    },
  });

  const {
    logCreditCardResponseError,
    logRssResponseError,
    logCampaignRunResponse,
    campaignRunResponse,
    logInternalServerError,
    internalServerError
  } = useSessionStorageLogger(formik, user);

  useEffect(() => {
      !campaignRunResponse && !user && sessionStorage.clear();
  }, [campaignRunResponse, user])

  // Capture referral code from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    const useCreditParam = params.get('uc');
    const amountParam = params.get('aс');
    if (useCreditParam === 'true') {
      setUseCredit(true);
      
      // Check if amount parameter exists and validate against user's credit balance
      if (amountParam && user) {
        const requestedAmount = parseFloat(amountParam);
        const userCreditBalance = user.credit_balance || 0;
        
        if (requestedAmount > userCreditBalance) {
          toast.error(`Insufficient credit balance. You requested $${requestedAmount.toFixed(2)} but only have $${userCreditBalance.toFixed(2)} available.`);
          setCreditBalance(userCreditBalance);
        } else {
          setCreditBalance(requestedAmount);
        }
      }
    }
    
    // Check if logged-in user is trying to use a referral code
    if (!isNewUser && user && refCode) {
      toast.info('You are already logged in. Referral codes can only be used during signup. Please log out first if you want to use this referral code.');
      // Remove the ref parameter from URL
      history.replace(location.pathname);
      return;
    }
    
    if (refCode) {
      setReferralCode(refCode);
    }
  }, [location, user, history]);

  useEffect(() => {
    const sendLogsWithInternalError = async () => {
      await createErrorsLog(parseSessionStorage());
      sessionStorage.clear();
    };

    internalServerError && sendLogsWithInternalError();
  }, [internalServerError]);

  useEffect(() => {
    const sendLogsOnCampaignRun = async () => {
      await createErrorsLog(parseSessionStorage());
      sessionStorage.clear();
    };

    campaignRunResponse && sendLogsOnCampaignRun();
  }, [campaignRunResponse]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const sendLogsBeforeUnload = async event => {
      setTimeout(async () => {
        await createErrorsLog(parseSessionStorage(true));
      }, 0)

    };

    window.addEventListener('beforeunload', sendLogsBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', sendLogsBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rellax = new Rellax('.rellax', {
      center: true,
    });
    if (user?.referrer && user?.referrer.active) {
      setDiscount(() => user.referrer);
    }

    if (params.has('rss')) {
      setRssWithNewEpisode(params.get('rss'));
    }
    if (params.has('referrer')) {
      getReferrer(params.get('referrer'))
        .then(response => {
          setDiscount(response);
        })
        .catch(e => {});
    }
    getUserCard()
      .then(result => {
        setSavedPaymentMethod(result);
        formik.setFieldValue('useSavedPaymentMethod', !!result);
        formik.setFieldValue('cardTypeForSavedPayment', result?.brand);
      })
      .catch(e => {
        console.error(e);
      });

    StackAdaptPixel();
    return () => {
      rellax.destroy();
    };
  }, [user]);

  useEffect(() => {
    if (rssWithNewEpisode) {
      formik.setFieldValue('rss', rssWithNewEpisode);
    }
  }, [rssWithNewEpisode]);

  useEffect(() => {
    formik.setFieldValue('logined', !!user);
  }, [user]);

  useEffect(() => {
    if (formik.values.rss) {
      formik.validateField('rss');
      const timeOutId = setTimeout(() => {
        setLoading(true);
        if (formik.errors.rss) {
          setLoading(false);
          setEpisodes([]);
          const { rss, boost, ...rest } = initialValues;
          formik.setValues({ ...formik.values, ...rest });
          return;
        }
        return getRssFeed(formik.values.rss)
          .then(result => {
            const { items, ...rest } = result;
            setLoading(false);
            setEpisodes(items);
            setFeed(rest);
            formik.setFieldValue('episode', items[0]);
            formik.setFieldValue('title', items[0].title || '');
            formik.setFieldValue(
              'subtitle',
              items[0].itunes.subtitle || items[0].itunes.summary || items[0].contentSnippet || ''
            );
          })
          .catch(e => {
            setLoading(false);
            formik.setFieldValue('episode', {});
            setEpisodes([]);
            console.log('e :>> ', e);
            toast.error(`RSS Feed Link: ${e.response.statusText}`);

            logRssResponseError(true);
          });
      }, 1000);
      return () => clearTimeout(timeOutId);
    } else {
      setEpisodes([]);
      const {
        rss,
        email,
        password,
        cardNumber,
        cardDate,
        cardCvv,
        cardName,
        zipCode,
        boost,
        useSavedPaymentMethod,
        ...rest
      } = initialValues;
      formik.setValues({ ...formik.values, ...rest });
    }
  }, [formik.errors.rss, formik.values.rss]);

  useEffect(() => {
    if (textAreaRef.current) {
      resize(textAreaRef.current);
    }
  }, [textAreaRef, formik.values.subtitle]);

  useEffect(() => {
    if (!formik.isSubmitting && Object.keys(formik.errors).length) {
      Object.keys(formik.errors).forEach(item => toast.error(formik.errors[item]));
    }
  }, [formik.isSubmitting]);

  const getSubtitle =
    formik.values?.episode?.itunes?.subtitle ||
    formik.values?.episode?.itunes?.summary ||
    formik.values?.episode?.contentSnippet;

  useEffect(() => {
    setTitles(null);
    setSubtitles(null);
    if (formik.values.episode.title) {
      getSuggestions(getSubtitle);
    }
  }, [formik.values.episode]);

  const handleRssFeed = e => {
    const { value } = e.target;
    formik.setFieldValue('rss', value);
  };

  const reCaptcha = value => {
    formik.setFieldValue('reCaptcha', true);
    setTimeout(() => {
      setShowRecaptcha(false);
    }, 1000);
  };

  const handleEpisode = item => {
    formik.setFieldValue('episode', item);
    formik.setFieldValue('title', item.title || '');
    formik.setFieldValue(
      'subtitle',
      item.itunes.subtitle || item.itunes.summary || item.contentSnippet || ''
    );
  };

  const handleBoost = value => {
    if (!value) {
      return formik.setFieldValue('boost', 100);
    }
    return formik.setFieldValue('boost', value);
  };

  const handleSubmit = async () => {
    setLoading(true);
    await formik.submitForm();
    setLoading(false);
  };

  const handleContinueWithoutReferral = async () => {
    setShowReferralUsedPopup(false);
    setReferralCode(null);
    setLoading(true);
    try {
      const { email, password } = pendingSignupData;
      const data = await signUp({
        email: email.toLowerCase(),
        password,
        source: partnerName,
        referralCode: null
      });
      setReferralCode(null);
      setUser(data.user);
      // Continue with campaign creation
      formik.submitForm();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Signup failed');
      setLoading(false);
    }
  };

  const handleCancelSignup = () => {
    setReferralCode(null);
    setShowReferralUsedPopup(false);
    setPendingSignupData(null);
    setLoading(false);
  };

  return (
    <>
      <div className="backgrounds">
        <div className="background__rainbow">
          <div className="background__rainbow--image rainbow--first rellax" />
          <div className="background__rainbow--image rainbow--second rellax" />
          <div className="background__rainbow--image rainbow--third rellax" />
        </div>
        <div className="background-sun" />
      </div>
      <Header />
      <div className="boost-page title">
        <h1 className="rotate" data-aos="fade-up" data-aos-delay="500" data-aos-duration="300">
          Get more IAB2.0 <br />
          downloads to your podcast!
        </h1>
      </div>
      <main>
        <img className="airpod-right rellax" data-rellax-speed="2" src={airpodsRight} alt="airpod-right" />
        <img className="airpod-left rellax" data-rellax-speed="2" src={airpodsLeft} alt="airpod-left" />
        <FormikProvider value={formik}>
          <form className="main container" autoComplete="off">
            <div className="title-header">
              <div className="title-form">
                {!user && referralCode && (
                  <div className="referral-badge">
                    <strong>Referral Applied!</strong> You'll get $50 off your first campaign of $100 or more!
                  </div>
                )}
                {useCredit && creditBalance > 0 && (
                  <div className="referral-badge">
                    <strong>Credit Applied!</strong> ${creditBalance.toFixed(2)} in credit will be applied to your campaign!
                  </div>
                )}
                {customMessageForRefer[discount?.code.toLowerCase()] && (
                  <div>
                    <label className="orange-text">
                      {customMessageForRefer[discount?.code.toLowerCase()].title}
                    </label>
                    <div className="how-this-works">
                      {customMessageForRefer[discount?.code.toLowerCase()].message}
                    </div>
                  </div>
                )}
                <div>
                  <label className="orange-text">How this works?</label>
                  <div className="how-this-works">{howThisWorks}</div>
                </div>
                <div>
                  <label htmlFor="title-form__input-rss" className="orange-text">
                    RSS Feed Link
                  </label>
                  <div className="position-relative">
                    <Field
                      type="url"
                      className="title-form__input-rss"
                      onChange={handleRssFeed}
                      id="title-form__input-rss"
                      name="rss"
                      placeholder={url || 'https://example.com/your-podcast/'}
                      pattern="https?://.*"
                      size="30"
                    />
                    {loading && <FontAwesomeIcon className="loading-spin" icon={faSpinner} size="lg" spin />}
                  </div>
                  {formik.errors.rss && <div className="fieldError errorColor">{formik.errors.rss}</div>}
                  {!!episodes.length && (
                    <>
                      <label htmlFor="title-form__input-episode" className="orange-text">
                        Episode
                      </label>
                      <div className="episodes-list" onClick={() => setOpenEpisodes(!openEpisodes)}>
                        <button type="button" className="dropdown_button">
                          {formik.values.episode.title || ''}
                        </button>
                        <nav id="episodes_id" className={cx('episodes', { 'show-episodes': openEpisodes })}>
                          {episodes.map((item, i) => (
                            <li key={i} onClick={() => handleEpisode(item)}>
                              {item.title}
                            </li>
                          ))}
                        </nav>
                      </div>
                    </>
                  )}
                </div>
              </div>
              {formik.values.episode.title && (
                <>
                  <EpisodeDescription formik={formik} feed={feed}/>
                  <div>
                    <section className="title-form title-form-text">
                      <label htmlFor="title-form__input-title" className="orange-text">
                        Title
                      </label>
                      <ErrorMessage name="title">
                        {msg => <div className="fieldError errorColor">{msg}</div>}
                      </ErrorMessage>
                      <div className="position-relative title-form-text">
                        <Field
                          as="textarea"
                          name="title"
                          className="title-form__input-title"
                          id="title-form__input-title"
                          placeholder="Enter podcast title"
                        />
                        <span
                          className={cx('title-form__input-title--display', {
                            errorColor: formik.values.title.length > 46,
                          })}
                        >
                          {formik.values.title.length}/46
                        </span>
                      </div>
                      <Grid
                        className="suggestions"
                        container
                        item
                        md={12}
                        alignItems="flex-start"
                        justifyContent="flex-end"
                      >
                        <CompressText
                          text={getSubtitle}
                          originalText={formik.values.episode.title}
                          numberOfCharacters={46}
                          handleOption={title => formik.setFieldValue('title', title)}
                          type="title"
                        />
                      </Grid>
                      <Grid container item md={12} justifyContent="space-between" alignItems="center">
                        <label htmlFor="title-form__input-subtitle" className="orange-text">
                          Subtitle
                        </label>
                      </Grid>
                      <ErrorMessage name="subtitle">
                        {msg => <div className="fieldError errorColor">{msg}</div>}
                      </ErrorMessage>
                      <div className="position-relative">
                        <Field
                          as="textarea"
                          name="subtitle"
                          innerRef={textAreaRef}
                          value={formik.values.subtitle}
                          className="title-form__input-subtitle"
                          id="title-form__input-subtitle"
                          placeholder="Enter podcast subtitle"
                        />
                        <span
                          className={cx('title-form__input-subtitle--display', {
                            errorColor: formik.values.subtitle.length > 120,
                          })}
                        >
                          {formik.values.subtitle.length}/120
                        </span>
                      </div>
                      <Grid className="suggestions" container item xs={12} justifyContent="flex-end">
                        <CompressText
                          text={getSubtitle}
                          numberOfCharacters={120}
                          handleOption={subtitle => formik.setFieldValue('subtitle', subtitle)}
                          type="subtitle"
                        />
                      </Grid>
                    </section>
                    <Advanced formik={formik} />
                  </div>
                </>
              )}
            </div>
            <div className="boost">
              <div className="boost__container">
                <div className="boost__counter">
                  {(referralCode && !showReferralUsedPopup) || (useCredit && creditBalance > 0) ? (
                    <p className="boost__counter--value orange-text">
                      <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '0.8em' }}>
                        ${formik.values.boost}
                      </span>
                      {' '}
                      ${calculateFinalPrice(formik.values.boost)}
                    </p>
                  ) : (
                    <p className="boost__counter--value orange-text">${formik.values.boost}</p>
                  )}
                  <div className="boost__counter--minmax">
                    <span>$100</span><span>$1000</span>
                  </div>
                  <RangeSlider min={100} max={1000} onChange={handleBoost} />
                  <span>
                    $
                    {discount?.price
                      ? adjustDownloadPrice(discount.price.toFixed(2), formik.values.boost)
                      : adjustDownloadPrice(costPerDownload, formik.values.boost)}
                    per download ={' '}
                    <span className="boost__counter--sum orange-text">
                      <br />
                      {boostInput(formik.values.boost, discount?.price)} Downloads
                    </span>
                  </span>
                  <ErrorMessage name="boost">
                    {msg => <div className="fieldError errorColor">{msg}</div>}
                  </ErrorMessage>
                </div>
              </div>
            </div>
            <CreditCard savedPaymentMethod={savedPaymentMethod} loading={loading} formik={formik} user={user}/>
            {!user && (
              <div className="card__login">
                <div className="card-input--login">
                  <div className="card-input--mail">
                    <label htmlFor="user_login">Email address</label>
                    <Field type="email" name="email" id="user_login" placeholder="email" />
                    <ErrorMessage name="email">
                      {msg => (
                        <div className="fieldError errorColor" style={{ margin: '0 10px' }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                  <div className="card-input--password">
                    <label htmlFor="user_password">Password</label>
                    <Field type="password" name="password" id="user_password" placeholder="•••••••••••••••" />
                    <ErrorMessage name="password">
                      {msg => (
                        <div className="fieldError errorColor" style={{ margin: '0 10px' }}>
                          {msg}
                        </div>
                      )}
                    </ErrorMessage>
                  </div>
                </div>
              </div>
            )}
            <div className="submit-boost">
              <h1>
                Total to be billed:{' '}
                {(referralCode && !showReferralUsedPopup) || (useCredit && creditBalance > 0) ? (
                  <span className="boost-sum orange-text">
                    <br />
                    <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '0.8em' }}>
                      ${formik.values.boost}
                    </span>
                    {' '}
                    ${calculateFinalPrice(formik.values.boost)}
                  </span>
                ) : (
                  <span className="boost-sum orange-text">
                    <br />
                    {formik.values.boost}
                  </span>
                )}
              </h1>
              <span className="submit-boost--description">
                Boost will go into queue once and is expected to go live within a few hours. Cancellations
                MUST be done within 60 minutes of purchase by emailing{' '}
                <a href="mailto:info@mowmedia.com">info@mowmedia.com</a> to obtain a full refund, during
                regular business hours of 9am-5pm EST. You will be billed for all downloads received if you
                cancel during the duration of the campaign, or for campaigns running after business hours.{' '}
                {textBottom ||
                  `Downloads will appear as mozilla, chrome, or mowpod.com in your${
                    partnerName === 'megaphone' ? ' Megaphone' : ' host'
                  }. `}
                There are no refunds for completed campaigns. This page serves as your Ad Insertion Order
                (IO). You must also review and acknowledge that you understand and agree to the Terms Of Use
                below.
              </span>
              <div className="agree-terms">
                <div className="checkbox--container">
                  <div className="agree-terms--checkbox">
                    <Field type="checkbox" name="agreeTerms" id="agree-terms--checkbox" />
                    <label htmlFor="agree-terms--checkbox"></label>
                  </div>
                  <span>
                    I understand <Modal />
                  </span>
                  <ErrorMessage name="agreeTerms">
                    {msg => (
                      <div className="fieldError errorColor" style={{ margin: '0 10px' }}>
                        {msg}
                      </div>
                    )}
                  </ErrorMessage>
                </div>

                <Collapse in={showRecaptcha} timeout={800}>
                  <Fade in={showRecaptcha} timeout={600}>
                    <div>
                      <ReCAPTCHA
                        sitekey="6LfZOWcgAAAAAB0_xFw_RDaz5Y1r9eO5zYslsIDd"
                        onChange={reCaptcha}
                        theme="dark"
                      />
                    </div>
                  </Fade>
                </Collapse>
                <button type='button' className="submit-boost--button submit-button" onClick={handleSubmit} disabled={loading}>
                  {loading ? <FontAwesomeIcon icon={faSpinner} size="2x" spin /> : 'Boost now'}
                </button>
                {loading && <h3>Creating Campaign - Stay On This Page!</h3>}
              </div>
            </div>
          </form>
        </FormikProvider>
      </main>
      
      <Dialog
        open={showReferralUsedPopup}
        onClose={handleCancelSignup}
      >
        {/* <DialogTitle>
          <h2 style={{ color: '#f49a03', margin: 0 }}>Referral Already Used</h2>
        </DialogTitle> */}
        <DialogContent>
          <p style={{ color: '#666' }}>
            This referral discount if for new customers only. Proceed at full price of ${formik.values.boost}?
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleContinueWithoutReferral} style={{ background: '#f49a03', color: 'white' }} variant="contained">
            YES
          </Button>
          <Button onClick={handleCancelSignup} style={{ color: '#666' }}>
            NO
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
